/* eslint-disable import/no-unresolved */
import React, { useEffect } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import apptracker from '@utils/sensor/apptracker';
import { PROMPT_ANSWER } from '@src/utils/sensor/track';
import DashboardTabs from '@components/common/Dashboard/DashboardTabs';
import ChatThread from '@components/prompt/chat-thread/ChatThread';
import ChatThreadBgPlaceholder from '@components/prompt/chat-thread/ChatThreadBgPlaceholder';
import PopularQuestions from '@components/prompt/popular-questions/PopularQuestions';
import MessageField from '@components/prompt/message-field/MessageField';
import DisclaimerText from '@components/prompt/disclaimer-text/DisclaimerText';
import { chatFeedWriteAtom, lastChatAtom, chatFeedAtom } from '@components/prompt/atoms';
import { getBotAnswerText, botAnswerHasMetricData } from '@components/prompt/utils';
import useGetStreamResponse from './hooks/useGetStreamResponse';
import {
  ContentWrapper,
  LeftSection,
  PromptWrapper,
  RightSection,
  RightSectionContent,
} from './styled';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import ReleaseVersion from '@components/common/ReleaseVersion';

function Prompt() {
  const { isLoading, hasStreamingEnded, error, botData, getStream, queryStartTime } =
    useGetStreamResponse('conversation');
  const chatFeed = useAtomValue(chatFeedAtom);
  const updateLastChatBlock = useSetAtom(chatFeedWriteAtom);
  const lastChatObj = useAtomValue(lastChatAtom);

  const isChatEmpty = chatFeed.length === 0;

  useEffect(() => {
    // if error set error for that chat
    if (error.title) {
      updateLastChatBlock({
        error,
        timestamp: Date.now(),
      });
    } else {
      // if no error set the response text
      // https://conviva.atlassian.net/browse/AAA-316
      // when switch back from another page url, the last response copy will be lost
      if (!lastChatObj.timestamp || botData?.length) {
        updateLastChatBlock({
          botData,
        });
      }
    }

    if (hasStreamingEnded) {
      updateLastChatBlock({
        timestamp: Date.now(),
      });
      const responseText = getBotAnswerText(botData);
      const timeCost = Date.now() - queryStartTime;
      apptracker(PROMPT_ANSWER, {
        questionAndAnswer: `${lastChatObj.userMsg}|||${responseText}`,
        hasMetric: botAnswerHasMetricData(botData) ? 'yes' : 'no',
        e2eTimeCost: timeCost,
        charactersPerSecond: (responseText.length / (timeCost / 1000)).toFixed(1),
      });
    }
  }, [updateLastChatBlock, hasStreamingEnded, error, lastChatObj.userMsg, botData]);

  return (
    <PromptWrapper>
      <DashboardTabs />
      <ErrorBoundary>
        <ReleaseVersion type={'Alpha'} />
        <ContentWrapper>
          {!isChatEmpty ? (
            <LeftSection>
              <h3>Common Questions</h3>
              <PopularQuestions disabled={isLoading} getStream={getStream} />
            </LeftSection>
          ) : null}
          <RightSection
            style={{
              width: !isChatEmpty ? '75%' : '100%',
              padding: !isChatEmpty ? '24px' : '24px 64px',
              overflow: !isChatEmpty ? 'hidden' : 'auto',
            }}
          >
            <RightSectionContent
              style={{
                width: !isChatEmpty ? 'calc(100% + 50px)' : '100%',
                paddingLeft: !isChatEmpty ? '150px' : '0',
              }}
            >
              {isChatEmpty ? (
                <ChatThreadBgPlaceholder getStream={getStream} />
              ) : (
                <ChatThread getStream={getStream} isLoading={isLoading} />
              )}
              <MessageField
                style={{ width: !isChatEmpty ? '725px' : '100%' }}
                disabled={isLoading}
                getStream={getStream}
              />
              <DisclaimerText style={{ width: !isChatEmpty ? '725px' : 'auto' }} />
            </RightSectionContent>
          </RightSection>
        </ContentWrapper>
      </ErrorBoundary>
    </PromptWrapper>
  );
}

export default Prompt;
